import { TalkRadarDetail } from "./TalkRadarDetail";
import { TalkRadarList } from "./TalkRadarList";
import { TalkTeacherDeck } from "./TalkTeacherDeck";
import type { RadarBubble } from "@/services/api/teacher";
import type { RadarCardKind } from "./TalkRadarDisc";
import { formatDay, useRadarStatusLabel } from "./talkRadarReadFormat";
import { talkRadarStageStyle } from "./talkRadarStage";

export type RadarReadStage = "list" | "detail" | "deck";

function requestIdsIn(bubbles: RadarBubble[]): Set<string> {
  const ids = new Set<string>();
  for (const bubble of bubbles) {
    const list =
      bubble.request_ids && bubble.request_ids.length > 0
        ? bubble.request_ids
        : bubble.request_id
          ? [bubble.request_id]
          : [];
    for (const id of list) ids.add(id);
  }
  return ids;
}

export function TalkRadarRead({
  role,
  stage,
  requestIds,
  requestId,
  kind = "incoming",
  requestStatus = null,
  requestDate = null,
  onOpen,
  onAssignLesson,
  onStornoBack,
  onEdgeBack,
  onMessage,
  incoming,
  bubbles = [],
  personId = null,
}: {
  role: "admin" | "teacher";
  stage: RadarReadStage;
  requestIds: string[];
  requestId: string | null;
  kind?: RadarCardKind;
  requestStatus?: string | null;
  requestDate?: string | null;
  onOpen: (
    id: string,
    kind?: RadarCardKind,
    meta?: { status?: string | null; date?: string | null }
  ) => void;
  onAssignLesson?: (
    lessonId: string,
    assignedTeacherName?: string | null
  ) => void;
  onStornoBack?: (abort: (() => void) | null) => void;
  onEdgeBack?: () => void;
  onMessage?: () => void;
  incoming?: boolean;
  bubbles?: RadarBubble[];
  personId?: string | null;
}) {
  return (
    <div className="talk-radar-read" style={talkRadarStageStyle}>
      {stage === "deck" && role === "teacher" ? (
        <TalkTeacherDeck bubbles={bubbles} onOpen={onOpen} />
      ) : stage === "deck" && role === "admin" ? (
        <TalkPersonDeck
          bubbles={bubbles}
          personId={personId}
          requestIds={requestIds}
          onOpen={onOpen}
        />
      ) : stage === "list" ? (
        <TalkRadarList
          requestIds={requestIds.filter((id) => requestIdsIn(bubbles).has(id))}
          onOpen={(id, meta) => onOpen(id, kind, meta)}
          role={role}
          kind={kind}
        />
      ) : (
        <TalkRadarDetail
          role={role}
          requestId={requestId}
          kind={kind}
          requestStatus={requestStatus}
          requestDate={requestDate}
          incoming={incoming}
          onAssignLesson={onAssignLesson}
          onStornoBack={onStornoBack}
          onEdgeBack={onEdgeBack}
          onMessage={onMessage}
        />
      )}
    </div>
  );
}

function TalkPersonDeck({
  bubbles,
  personId,
  requestIds,
  onOpen,
}: {
  bubbles: RadarBubble[];
  personId: string | null;
  requestIds: string[];
  onOpen: (
    id: string,
    kind?: RadarCardKind,
    meta?: { status?: string | null; date?: string | null }
  ) => void;
}) {
  const statusLabel = useRadarStatusLabel();
  const mine = personId
    ? bubbles.filter((bubble) => bubble.requester_teacher_id === personId)
    : bubbles.filter((bubble) => {
        const ids =
          bubble.request_ids && bubble.request_ids.length > 0
            ? bubble.request_ids
            : bubble.request_id
              ? [bubble.request_id]
              : [];
        return ids.some((id) => requestIds.includes(id));
      });
  const cards = new Map<
    string,
    { title: string; status: string; date: string | null; meta: string }
  >();
  for (const bubble of mine) {
    const ids =
      bubble.request_ids && bubble.request_ids.length > 0
        ? bubble.request_ids
        : bubble.request_id
          ? [bubble.request_id]
          : [];
    for (const id of ids) {
      if (cards.has(id)) continue;
      cards.set(id, {
        title: bubble.requester_name || bubble.subject_name || "Anfrage",
        status: bubble.status || "",
        date: bubble.lesson_date,
        meta: [
          formatDay(bubble.lesson_date),
          bubble.subject_name,
          bubble.klass_name,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }
  }
  if (cards.size === 0) {
    for (const id of requestIds) {
      cards.set(id, { title: "Anfrage", status: "", date: null, meta: "" });
    }
  }
  return (
    <div className="talk-teacher-stage">
      <p className="talk-deck-title">Karten</p>
      <ul className="talk-radar-list">
        {[...cards.entries()].map(([id, card]) => (
          <li key={id}>
            <button
              type="button"
              className="talk-radar-card"
              onClick={() =>
                onOpen(id, undefined, { status: card.status, date: card.date })
              }
            >
              <span className="talk-radar-card-top">
                <span>{card.title}</span>
                {card.status ? (
                  <span className="talk-pill">{statusLabel(card.status)}</span>
                ) : null}
              </span>
              {card.meta ? (
                <span className="talk-radar-card-title">{card.meta}</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
